import React, { useContext, useMemo } from "react";
import { Alert, Box, Card, Divider, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_SX,
} from "../../../components/ui/dataGridStyles";
import { StateContext } from "../../../components/context/StateProvider";
import { EstadosComprasMobileList } from "../components/lists";
import { INTERVALO_REFRESCO_COMPRAS_MS } from "../constants";
import { ESTADOS_COMPRAS_COLUMNS } from "../helpers/columnsEstadosCompras";
import { prepararFilasEstadosCompras } from "../helpers/estadosCompras";
import usePedidosCompra from "../hooks/usePedidosCompra";

const MENSAJE_ERROR = "No se pudieron cargar los estados de compras";
const SX_CARD = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 };
const SX_TITULO = { fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 };
const SX_SUBTITULO = { color: "#6c757d" };
const getRowId = (fila) => fila.id;
// Se revisa el listado completo de un vistazo: 100 filas por página
const INITIAL_STATE = { pagination: { paginationModel: { pageSize: 100 } } };

/**
 * Página "Control pedidos compra": pedidos de compra pendientes del ejercicio,
 * con refresco silencioso periódico y vista móvil en tarjetas
 */
const EstadosCompras = () => {
    const { state } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const { pedidos, loading, error } = usePedidosCompra(state.year, {
        intervaloMs: INTERVALO_REFRESCO_COMPRAS_MS,
    });
    const filas = useMemo(() => prepararFilasEstadosCompras(pedidos), [pedidos]);

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={SX_CARD}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={SX_TITULO}>
                        <FactCheckIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Control pedidos compra
                    </Typography>
                    <Typography variant="body2" sx={SX_SUBTITULO}>
                        Pedidos de compra pendientes del ejercicio {state.year}
                    </Typography>
                    {error !== null && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="error">{error || MENSAJE_ERROR}</Alert>
                        </>
                    )}
                </Box>
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {isMobile ? (
                        <EstadosComprasMobileList rows={filas} loading={loading} />
                    ) : (
                        <DataGrid
                            rows={filas}
                            columns={ESTADOS_COMPRAS_COLUMNS}
                            getRowId={getRowId}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            initialState={INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default EstadosCompras;
